
















































































































































































































import Vue, { PropType } from 'vue'
import XDropdown, {
  OptionEntry,
} from '@/components/atoms/Dropdown/XDropdown.vue'
import XPrice from '@/components/atoms/Price/XPrice.vue'
import XHeading from '@/components/atoms/XHeading/XHeading.vue'
import XButton from '@/components/atoms/Button/XButton.vue'
import QuantityInput from '@/components/molecules/FfGoods/QuantityInput.vue'
import {
  Cart,
  CreditCard,
  DeliveryTimeCandidate,
  Partner,
  PaymentMethod,
  StoreSalesGoods,
  StoreShippingAddress,
} from '@/types/generated/graphql'
import gql from 'graphql-tag'
import { GraphQLError } from 'graphql/error/GraphQLError'
import GoodsPriceLoader from '@/components/molecules/FfGoods/Lib/GoodsPriceLoader'
import StorePicker from '@/components/molecules/StorePicker/StorePicker.vue'
import StoreAddressPicker from '@/components/molecules/StoreAddressPicker/StoreAddressPicker.vue'
import formatDate from '@/plugins/filters/formatDate'
import XIcon from '@/components/atoms/Icon/XIcon.vue'
import XFormLabel from '@/components/atoms/FormLabel/XFormLabel.vue'

type PriceInfoType = {
  totalUnitPrice: number
  shippingPrice: number
  totalTaxPrice: number
  totalPriceIncludeTax: number
}

type DataType = {
  paymentMethod: typeof PaymentMethod
  orderQuantity: number
  storeId?: string
  storeShippingAddressId?: string
  storeShippingAddress?: StoreShippingAddress
  displayAddress: {
    address: string
    destinationName: string
    phone: string | null | undefined
  }
  deliveryTimeCandidate: Array<DeliveryTimeCandidate>
  deliveryDate: string
  deliveryTimeSlot: number | undefined
  priceInfo: PriceInfoType
  goodsPriceLoader: GoodsPriceLoader
  error: boolean
  errorMessage: string
  addressPickerPlaceholder?: string
}

export default Vue.extend({
  name: 'OrderConfirmation',
  components: {
    XDropdown,
    XPrice,
    XHeading,
    XButton,
    XIcon,
    XFormLabel,
    QuantityInput,
    StorePicker,
    StoreAddressPicker,
  },
  data: function (): DataType {
    return {
      paymentMethod: PaymentMethod,
      orderQuantity: this.quantity,
      storeId: undefined,
      storeShippingAddressId: undefined,
      storeShippingAddress: undefined,
      displayAddress: {
        address: '',
        destinationName: '',
        phone: '',
      },
      deliveryDate: '',
      deliveryTimeCandidate: [],
      deliveryTimeSlot: undefined,
      priceInfo: {
        totalUnitPrice: 0,
        shippingPrice: 0,
        totalTaxPrice: 0,
        totalPriceIncludeTax: 0,
      },
      goodsPriceLoader: new GoodsPriceLoader(this),
      error: false,
      errorMessage: '',
      addressPickerPlaceholder: undefined,
    }
  },
  props: {
    partner: {
      type: Object as PropType<Partner>,
      required: true,
    },
    salesFfGoods: {
      type: Object as PropType<StoreSalesGoods>,
      required: true,
    },
    creditCard: {
      type: Object as PropType<CreditCard>,
      required: false,
    },
    quantity: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    canOrder(): boolean {
      return (
        this.orderQuantity > 0 &&
        this.deliveryDate !== '' &&
        this.deliveryTimeSlot !== undefined &&
        !!this.storeShippingAddressId
      )
    },
    deliveryDateOptions(): Array<OptionEntry<string>> {
      return this.deliveryTimeCandidate.reduce(
        (options, candidate) => {
          return options.concat([
            {
              value: candidate.date,
              label: formatDate(candidate.date, { withWeek: true }),
            },
          ])
        },
        [{ value: '', label: '' }]
      )
    },
    deliveryTimeSlotOptions(): Array<OptionEntry<number>> {
      const candidate = this.deliveryTimeCandidate.find((deliveryTime) => {
        return deliveryTime.date === this.deliveryDate
      })

      if (!candidate) {
        return []
      }

      const initial: Array<OptionEntry<number>> = []
      return candidate.deliveryTimeSlots.reduce((options, timeSlot) => {
        return options.concat([{ value: timeSlot.code, label: timeSlot.label }])
      }, initial)
    },
  },
  methods: {
    onStoreChanged() {
      this.storeShippingAddressId = undefined
      this.storeShippingAddress = undefined
    },
    onStoreShippingAddressChanged() {
      if (!this.storeShippingAddressId) {
        this.deliveryDate = ''
        this.deliveryTimeSlot = undefined
      }
    },
    onAddressLoaded(values: StoreShippingAddress[]) {
      this.addressPickerPlaceholder = values.length
        ? undefined
        : this.$tc('ffGoodsList.notRegisterShippingAddress')
    },
    onDeliveryDateChanged() {
      if (this.deliveryDate === '') {
        this.deliveryTimeSlot = undefined
        return
      }

      const candidate = this.deliveryTimeCandidate.find(
        (candidate: DeliveryTimeCandidate) => {
          return candidate.date === this.deliveryDate
        }
      ) as DeliveryTimeCandidate

      this.deliveryTimeSlot = candidate.deliveryTimeSlots[0].code
    },
    async getDeliveryTimeCandidate(zipCode: string) {
      const { data } = await this.$apollo.query({
        query: gql`
          query DeliveryTimeCandidate($input: DeliveryTimeCandidateInput!) {
            deliveryTimeCandidate(input: $input) {
              date
              deliveryTimeSlots {
                code
                label
              }
            }
          }
        `,
        variables: {
          input: {
            zipCode: zipCode,
            shippingDate: this.salesFfGoods.shippingDate,
          },
        },
      })

      this.deliveryTimeCandidate = data.deliveryTimeCandidate
    },
    async loadPrice() {
      const loadResult = await this.goodsPriceLoader.loadPrice(
        this.salesFfGoods.product.productId,
        this.orderQuantity
      )
      if (loadResult.isLoaded) {
        const cart = loadResult.result as Cart
        this.priceInfo = {
          totalUnitPrice: cart.pricing.price.net,
          shippingPrice: cart.pricing.shipping.net,
          totalTaxPrice: cart.pricing.total.tax,
          totalPriceIncludeTax: cart.pricing.total.gross,
        }
      }
    },
    async onClickOrder(): Promise<void> {
      this.$emit('order:before', async () => {
        await this.$apollo.mutate({
          mutation: gql`
            mutation OrderFfGoods($input: OrderFfGoodsInput!) {
              orderFfGoods(input: $input) {
                orderId
              }
            }
          `,
          variables: {
            input: {
              paymentMethod: this.partner.paymentMethod,
              partnerServiceContractId: this.$route.params.id,
              goodId: this.salesFfGoods.product.goodId,
              fulfillmentProcessId: this.salesFfGoods.fulfillmentProcessId,
              orderQuantity: this.orderQuantity,
              storeId: this.storeId,
              storeShippingAddressId: this.storeShippingAddressId,
              deliveryDate: this.deliveryDate,
              deliveryTimeSlot: this.deliveryTimeSlot,
              cardId:
                this.partner.paymentMethod === PaymentMethod.Credit
                  ? this.creditCard.cardId
                  : null,
            },
          },
          context: {
            errorHandler: (errorCode: string, error: GraphQLError) => {
              // 配送可能エリア外
              this.error = true
              this.errorMessage = error.message
            },
          },
        })

        this.$emit('order:done')
      })
    },
  },
  async mounted() {
    await this.loadPrice()
  },
  watch: {
    async orderQuantity(): Promise<void> {
      this.$emit('update:quantity', this.orderQuantity)

      await this.loadPrice()
    },
    storeShippingAddressId() {
      const target = this.storeShippingAddress
      if (target !== undefined) {
        this.displayAddress = {
          address: target.address.fullAddress,
          destinationName: target.destinationName,
          phone: this.$tc('common.tel') + '：' + target.phone,
        }
        this.getDeliveryTimeCandidate(target.address.zipCode.fullCode)
      } else {
        this.displayAddress = {
          address: '',
          destinationName: '',
          phone: '',
        }
      }
    },
  },
  apollo: {
    goodsMenus: {
      query: gql`
        query menus($goodsSpecId: ID!, $fulfillmentProcessId: ID!) {
          goodsMenus(
            goodsSpecId: $goodsSpecId
            fulfillmentProcessId: $fulfillmentProcessId
          ) {
            menuId
            menu {
              menuId
              menuName
            }
          }
        }
      `,
      variables() {
        return {
          goodsSpecId: this.salesFfGoods.product.goodsSpecId,
          fulfillmentProcessId: this.salesFfGoods.fulfillmentProcessId,
        }
      },
    },
  },
})
